import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { useContent } from "../../i18n/content";
import { KDS } from "../../data/kds";
import { SectionHeader } from "../SectionHeader";
import { Reveal, Counter } from "../motion/Reveal";

const pill = (active) =>
    `font-display text-[11px] font-bold tracking-[0.16em] uppercase px-5 py-2.5 border transition-colors duration-300 ${
        active
            ? "bg-[#f6c422] text-[#292a24] border-[#f6c422]"
            : "border-[#4a4c42] text-[#a2a59d] hover:border-[#f6c422] hover:text-[#f6c422]"
    }`;

const statusColor = (s) =>
    s === "Completed"
        ? "text-[#f6c422] border-[#f6c422]/40"
        : s === "Proposal"
          ? "text-white/80 border-white/25"
          : "text-[#ffe169] border-[#ffe169]/50";

export const Experience = () => {
    const { ui, companies } = useContent();
    const [filter, setFilter] = useState("all");
    const filtered = filter === "all" ? companies : companies.filter((c) => c.slug === filter);
    return (
    <section id="projects" data-testid="experience-section" className="relative py-24 lg:py-32 bg-[#24251f]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <SectionHeader
                chapter={ui.experience.chapter}
                overline={ui.experience.overline}
                title={ui.experience.title}
                description={ui.experience.description}
            />

            <Reveal>
                <div className="flex flex-wrap gap-2 mb-14" role="group" aria-label="Filter capabilities by company">
                    <button
                        data-testid="experience-filter-all"
                        onClick={() => setFilter("all")}
                        aria-pressed={filter === "all"}
                        className={pill(filter === "all")}
                    >
                        {ui.experience.allGroup}
                    </button>
                    {companies.map((c) => (
                        <button
                            key={c.slug}
                            data-testid={`experience-filter-${c.slug}`}
                            onClick={() => setFilter(c.slug)}
                            aria-pressed={filter === c.slug}
                            className={pill(filter === c.slug)}
                        >
                            {c.cardTitle}
                        </button>
                    ))}
                </div>
            </Reveal>

            <div className="space-y-20">
                {filtered.map((c, i) => (
                    <Reveal key={c.slug}>
                        <div
                            data-testid={`experience-block-${c.slug}`}
                            className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-center ${
                                i % 2 === 1 ? "" : ""
                            }`}
                        >
                            <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                                <div className="relative overflow-hidden border border-[#4a4c42] group">
                                    <img
                                        src={c.image}
                                        alt={`${c.fullName} project category`}
                                        className="w-full h-72 sm:h-80 object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                                        loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-[#292a24]/40" />
                                    <span className="absolute bottom-5 left-5 overline-text bg-[#292a24]/70 backdrop-blur-sm px-3 py-2 border border-white/10">
                                        {c.category}
                                    </span>
                                </div>
                            </div>

                            <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                                <p className="overline-text">{c.fullName}</p>
                                {c.slug === "digon" && (
                                    <div className="flex items-baseline gap-3 mt-4">
                                        <Counter
                                            value="20"
                                            suffix="+"
                                            className="font-display text-6xl font-extrabold text-[#f6c422]"
                                        />
                                        <span className="font-display text-sm uppercase tracking-[0.2em] text-[#a2a59d]">
                                            {ui.experience.years}
                                        </span>
                                    </div>
                                )}
                                <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                                    {c.experienceHighlights.map((h) => (
                                        <li key={h} className="flex items-start gap-3 text-sm text-white/85 leading-relaxed">
                                            <Check className="w-4 h-4 text-[#f6c422] shrink-0 mt-0.5" strokeWidth={2.5} />
                                            {h}
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to={c.route}
                                    data-testid={`experience-link-${c.slug}`}
                                    className="mt-8 inline-flex items-center gap-2 font-display font-bold text-[11px] tracking-[0.2em] uppercase text-[#f6c422] hover:gap-4 transition-[gap] duration-300"
                                >
                                    View Company Profile <ArrowUpRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>
                    </Reveal>
                ))}

                {filter === "kharisma-damar-satya" && (
                    <Reveal>
                        <div data-testid="experience-kds-portfolio" className="border border-[#4a4c42]">
                            <div className="px-6 py-5 border-b border-[#4a4c42] flex flex-wrap items-center justify-between gap-4 bg-[#24251f]">
                                <p className="overline-text">{ui.kds.portfolioTitle}</p>
                                <Link
                                    to={KDS.route}
                                    data-testid="experience-kds-open-profile"
                                    className="inline-flex items-center gap-2 font-display font-bold text-[11px] tracking-[0.2em] uppercase text-[#f6c422] hover:gap-4 transition-[gap] duration-300"
                                >
                                    {ui.experience.openKds} <ArrowUpRight className="w-4 h-4" />
                                </Link>
                            </div>
                            {KDS.projects.map((p) => (
                                <div
                                    key={p.num}
                                    data-testid={`experience-kds-project-${p.num}`}
                                    className="grid grid-cols-2 sm:grid-cols-12 gap-2 sm:gap-4 items-baseline px-6 py-4 border-b border-[#4a4c42] last:border-b-0 hover:bg-[#32332c]/50 transition-colors duration-300"
                                >
                                    <span className="sm:col-span-1 font-display text-xs text-[#a2a59d]/50 tracking-[0.2em]">{p.num}</span>
                                    <span className="sm:col-span-4 font-display text-sm font-bold text-white leading-snug">{p.project || p.client}</span>
                                    <span className="sm:col-span-2 text-xs text-[#a2a59d]">{p.location}</span>
                                    <span className="sm:col-span-2 text-xs text-[#a2a59d]">{p.scope}</span>
                                    <span className="sm:col-span-2">
                                        <span className={`inline-block text-[10px] font-display font-semibold tracking-wider uppercase border px-2 py-1 ${statusColor(p.status)}`}>
                                            {p.status}
                                        </span>
                                    </span>
                                    <span className="sm:col-span-1 text-xs font-display font-semibold text-[#f6c422]/90">{p.year}</span>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                )}
            </div>
        </div>
    </section>
    );
};
